import React, { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import { SizeType, VariantType } from '@/types/common';
import { cn } from '@/utils/cn';

import Spinner from './Spinner';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  text?: string | JSX.Element;
  variant?: VariantType;
  size?: SizeType;
  icon?: JSX.Element;
  disabled?: boolean;
  pending?: boolean;
  wide?: boolean;
  className?: string;
  href?: string;
  noBackground?: boolean;
  inverted?: boolean;
};

const getButtonConfigs = (inverted = false) => {
  return {
    primary: {
      bgColor: 'bg-brand-green',
      hoverColor: 'hover:bg-brand-green-hover',
      color: 'text-black dark:text-gray-100',
      ring: 'focus:ring-blue-500',
    },
    primaryLight: {
      bgColor: 'bg-brand-green-light',
      hoverColor: 'hover:bg-brand-green hover:text-gray-10',
      color: 'text-[#767B1E]',
    },
    primaryAlt: {
      bgColor: 'bg-brand-brown',
      hoverColor: 'hover:bg-brand-brown/80',
      color: 'text-white dark:text-gray-100',
      ring: 'focus:ring-blue-500',
    },
    secondary: {
      bgColor: 'bg-white dark:bg-gray-700',
      hoverColor: 'hover:bg-gray-300 dark:hover:bg-gray-600',
      color: 'text-black dark:text-gray-100',
      ring: 'focus:ring-gray-500',
    },
    secondaryAlt: {
      bgColor: 'bg-[#B9B9B9]',
      hoverColor: 'bg-[#B9B9B9]',
      color: 'text-white',
      ring: 'focus:ring-gray-500',
    },
    danger: {
      bgColor: 'bg-red-600',
      hoverColor: 'hover:bg-red-700',
      color: 'text-white dark:text-gray-100',
      ring: 'focus:ring-red-500',
    },
    ['danger-dark']: {
      bgColor: 'bg-red-700',
      hoverColor: 'hover:bg-red-800',
      color: 'text-white dark:text-gray-100',
      ring: 'focus:ring-red-600',
    },
    warning: {
      bgColor: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      color: 'text-white dark:text-gray-100',
      ring: 'focus:ring-orange-500',
    },
    disabled: {
      bgColor: 'bg-gray-400 dark:bg-gray-600',
      hoverColor: inverted ? 'hover:bg-gray-100 dark:hover:bg-gray-600' : 'hover:bg-gray-400 dark:hover:bg-gray-600',
      color: inverted ? 'text-black dark:text-white' : 'text-white dark:text-gray-100',
      ring: 'focus:ring-gray-500',
    },
    success: {
      bgColor: 'bg-green-600',
      hoverColor: 'hover:bg-green-700',
      color: 'text-white dark:text-gray-100',
      ring: 'focus:ring-green-500',
    },
  };
};

const buttonSizesConfig = {
  sm: 'min-h-[2rem] p-2',
  md: 'min-h-[3.5rem] px-4',
  lg: 'min-h-[4.5rem] px-4',
  xl: 'min-h-[5.5rem] px-6',
  '2xl': 'min-h-[6.5rem] px-6',
};

const mapButtonVariant = (color: VariantType, inverted: boolean): string => Object.values(getButtonConfigs(inverted)[color]).join(' ');

const ButtonTag = ({
  href,
  onClick,
  className,
  disabled,
  children,
  ...rest
}: PropsWithChildren<Pick<ButtonProps, 'href' | 'onClick' | 'className' | 'disabled'>>) => {
  if (href) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onClick?.(event);
  };

  return (
    <button onClick={handleClick} className={className} {...rest}>
      {children}
    </button>
  );
};

const Button = ({
  text,
  variant,
  size,
  icon,
  disabled,
  pending,
  wide,
  className,
  href,
  children,
  noBackground,
  inverted,
  ...rest
}: ButtonProps) => {
  const finalVariant = disabled ? 'disabled' : variant ?? 'primary';

  const buttonClassName = cn(
    'text-nowrap max-h-9 rounded-[8px] text-base cursor-pointer flex gap-2 m-0 items-center transition ease-in duration-200 justify-center text-center drop-shadow-sm relative',
    buttonSizesConfig[size || 'sm'],
    mapButtonVariant(finalVariant, inverted),
    {
      'cursor-not-allowed bg-opacity-80': disabled,
      'w-full': wide,
      'bg-transparent': noBackground,
    },
    className,
    { 'cursor-not-allowed': disabled },
  );

  return (
    <ButtonTag href={href} disabled={disabled} className={buttonClassName} {...rest}>
      {children}
      {icon && (
        <div className={`${pending ? 'opacity-0' : ''} flex flex-grow justify-center items-center gap-2`}>
          {icon}
          {text}
        </div>
      )}
      {pending && (
        <div className={'absolute left-1/3 right-1/3 top-1/3 bottom-1/3 flex items-center justify-center'}>
          <Spinner size={size} color={variant} />3{' '}
        </div>
      )}
    </ButtonTag>
  );
};

export default Button;
